import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingLocationType, BookingRecordDetailsCustomer, UserGroupType } from '@navio/nsdk';
import { NoddiIcon } from 'noddi-ui-common';
import { formatPhoneNumber, getUserDisplayName, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { DepartmentOrganizationName, getLongAddressName } from 'noddi-web';
import { useServiceCenterLocation } from '../../hooks/useServiceCenterLocation';
import { ServiceCenterLocation } from '../ServiceCenter/ServiceCenterLocation';

interface Props {
  booking: BookingRecordDetailsCustomer;
}

// Status, date and reference sit in the card header; these rows answer when, where and who.
export const BSlugBookingDetailsInfo = ({ booking }: Props) => {
  const deliveryTimeStartsAt = noddiFormatDate(booking.deliveryWindowStartsAt, NoddiDateFormats.TIME);
  const deliveryTimeEndsAt = noddiFormatDate(booking.deliveryWindowEndsAt, NoddiDateFormats.TIME);

  const deliveryTime = `${deliveryTimeStartsAt} - ${deliveryTimeEndsAt}`;
  const isOrganizationBooking = booking.userGroup.type === UserGroupType.ORGANIZATION;
  const bookingUserGroupName = booking.userGroup.name;
  const isStationary = booking.locationType === BookingLocationType.STATIONARY;

  const { name: serviceCenterName, address: serviceCenterAddress } = useServiceCenterLocation({
    garageId: booking.garageId,
    serviceOrganizationId: booking.serviceOrganizationId,
    fallbackName: booking.serviceDepartmentBrand.name,
    fallbackAddress: booking.address,
    enabled: isStationary
  });

  return (
    <div className='flex flex-col gap-3 text-sm'>
      {booking.hasCustomerVisit ? (
        <div className='flex items-center gap-3'>
          <NoddiIcon name='Clock' size='small' />
          <p>{deliveryTime}</p>
        </div>
      ) : (
        <div className='flex items-center gap-3'>
          <NoddiIcon name='Truck' size='small' />
          <p>
            <Trans>We pick up your tires from your current tire hotel</Trans>
          </p>
        </div>
      )}

      {isStationary && serviceCenterAddress ? (
        // Sized to match the other rows' small icons.
        <ServiceCenterLocation
          name={serviceCenterName}
          address={serviceCenterAddress}
          className='gap-3 [&>svg]:size-4'
        />
      ) : (
        <div className='flex items-center gap-3'>
          <NoddiIcon name='MapPin' size='small' />
          <p>{getLongAddressName(booking.address)}</p>
          <a
            href={booking.address.googleMapsUrl}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={t`Open in Google Maps (opens in a new tab)`}
            className='text-muted-foreground hover:text-primary'
          >
            <NoddiIcon name='Map' size='small' />
          </a>
        </div>
      )}

      <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
        <NoddiIcon name='User' size='small' />
        <p>{getUserDisplayName(booking.user)}</p>
        {booking.user.phoneNumber && (
          <p className='text-muted-foreground'>{formatPhoneNumber(booking.user.phoneNumber)}</p>
        )}
      </div>

      {isOrganizationBooking && (
        <div className='flex items-center gap-3'>
          <NoddiIcon name='Building2' size='small' />
          {booking.customerDepartmentName ? (
            <DepartmentOrganizationName
              departmentName={booking.customerDepartmentName}
              organizationName={booking.customerOrganizationName}
            />
          ) : (
            <p>
              <Trans>This booking has been made on behalf of company: {bookingUserGroupName}.</Trans>
            </p>
          )}
        </div>
      )}
    </div>
  );
};
